import {
  CountryInput,
  FieldWrapper,
  Input,
  InputErrorMessage,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@commenda/kakashi';
import { classNames } from '@commenda/utils';
import { Country } from '@prisma/client';
import { Country as CountryInfo, State } from 'country-state-city';
import { Controller, useFormContext } from 'react-hook-form';
import * as z from 'zod';

function getNestedValue(obj: any, path: string) {
  return path.split('.').reduce((acc, part) => acc && acc[part], obj);
}

export function getCountryOptions(): { name: string; code: Country }[] {
  return Object.values(Country)
    .filter((countryCode) => {
      const country = CountryInfo.getCountryByCode(countryCode);
      return country;
    })
    .sort((countryCodeA, countryCodeB) => {
      const countryA = CountryInfo.getCountryByCode(countryCodeA);
      const countryB = CountryInfo.getCountryByCode(countryCodeB);
      return countryA.name > countryB.name ? 1 : -1;
    })
    .map((countryCode) => {
      const country = CountryInfo.getCountryByCode(countryCode);
      return {
        code: countryCode,
        name: country.name,
      };
    });
}

function getStatesByCountry(
  country: string
): { code: string; displayedName: string }[] {
  if (!country) return [];

  return State.getStatesOfCountry(country).map((eachState) => ({
    code: eachState.isoCode,
    displayedName: eachState.name,
  }));
}

export const addressSchema = z
  .object({
    country: z.string().min(1, 'Country is required.'),
    addressLine1: z.string().trim().min(1, 'Address line 1 is required.'),
    addressLine2: z.string().trim().optional().nullable(),
    addressLine3: z.string().trim().optional().nullable(),
    state: z.string().trim().nullable().optional(),
    city: z.string().trim().min(1, 'City is required.'),
    postalCode: z.string().trim().min(1, { message: 'Zip code is required.' }),
  })
  .superRefine((data, ctx) => {
    const availableStates = getStatesByCountry(data.country);

    if (availableStates.length === 0) {
      return;
    }

    if (!data.state) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'State is required.',
        path: ['state'],
      });
    }
  });

export type AddressSchemaFields = z.infer<typeof addressSchema>;

interface AddressGroupProps {
  pathPrefix?: string;
  disabled?: boolean;
  hideCountry?: boolean;
  className?: string;
  /**
   * Optional backend-driven state options. When provided (even as an empty array)
   * the state dropdown is populated from these instead of the country-state-city
   * library, and an empty array hides the dropdown — used by the location forms so
   * the picker only offers subdivision codes the sales-tax validator accepts.
   * Leaving it `undefined` (the default) preserves library-driven behavior for
   * every other consumer of this component.
   */
  stateOptions?: { code: string; displayedName: string }[];
  /** Where the State field sits relative to the City/Zip row: 'prefix' (before) or 'suffix' (after). */
  statePlacement?: 'prefix' | 'suffix';
  /**
   * The whole address is optional, so every field says so. Off by default: the other consumers
   * validate against `addressSchema`, which requires country, line 1, city and zip.
   */
  optional?: boolean;
}

export default function AddressGroupRHF({
  pathPrefix = '',
  disabled = false,
  hideCountry = false,
  className = '',
  stateOptions,
  statePlacement = 'prefix',
  optional = false,
}: AddressGroupProps) {
  const {
    register,
    formState: { errors },
    watch,
    control,
  } = useFormContext();

  const getFieldPath = (field: keyof AddressSchemaFields): string =>
    pathPrefix ? `${pathPrefix}.${field}` : field;

  const getErrorMessage = (
    field: keyof AddressSchemaFields
  ): string | undefined => {
    const path = getFieldPath(field);
    return getNestedValue(errors, `${path}.message`);
  };

  const selectedCountry = watch(getFieldPath('country'));

  // Backend-provided options take precedence over the country-state-city library.
  const availableStates =
    stateOptions !== undefined
      ? stateOptions
      : selectedCountry
        ? getStatesByCountry(selectedCountry as string)
        : [];

  const stateBlock =
    availableStates.length > 0 ? (
      <FieldWrapper>
        <Label optional={optional}>State</Label>
        <Controller
          name={getFieldPath('state')}
          control={control}
          render={({ field }) => (
            <Select
              value={(field.value as string) || ''}
              onValueChange={(value) => {
                field.onChange(value);
              }}
              disabled={disabled}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a state" />
              </SelectTrigger>
              <SelectContent className="max-h-[240px]">
                {availableStates.map(({ code, displayedName }) => (
                  <SelectItem key={code} value={code}>
                    {displayedName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
        {getErrorMessage('state') && (
          <InputErrorMessage message={getErrorMessage('state')} />
        )}
      </FieldWrapper>
    ) : null;

  const cityZipBlock = (
    <div className="flex items-start gap-4">
      <div className="flex-1">
        <FieldWrapper>
          <Label optional={optional}>City</Label>
          <Input
            placeholder="Enter city name"
            {...register(getFieldPath('city'))}
            disabled={disabled}
            error={getErrorMessage('city')}
          />
        </FieldWrapper>
      </div>
      <div className="flex-1">
        <FieldWrapper>
          <Label optional={optional}>Zip Code</Label>
          <Input
            placeholder="Enter Zip code"
            {...register(getFieldPath('postalCode'))}
            disabled={disabled}
            error={getErrorMessage('postalCode')}
          />
        </FieldWrapper>
      </div>
    </div>
  );

  return (
    <div className={classNames('flex flex-col gap-xl', className)}>
      {!hideCountry && (
        <FieldWrapper>
          <Label optional={optional}>Country</Label>
          <Controller
            name={getFieldPath('country')}
            control={control}
            render={({ field }) => (
              <CountryInput
                disabled={disabled}
                value={field.value as string}
                type="single"
                onValueChange={(val) => field.onChange(val)}
                error={getErrorMessage('country')}
              />
            )}
          />
        </FieldWrapper>
      )}

      <FieldWrapper>
        <Label optional={optional}>Address Line 1</Label>
        <Input
          placeholder="Enter street address"
          {...register(getFieldPath('addressLine1'))}
          disabled={disabled}
          error={getErrorMessage('addressLine1')}
        />
      </FieldWrapper>

      <FieldWrapper>
        <Label optional>Address Line 2</Label>
        <Input
          placeholder="Optional"
          {...register(getFieldPath('addressLine2'))}
          disabled={disabled}
          error={getErrorMessage('addressLine2')}
        />
      </FieldWrapper>

      {statePlacement === 'suffix' ? (
        <>
          {cityZipBlock}
          {stateBlock}
        </>
      ) : (
        <>
          {stateBlock}
          {cityZipBlock}
        </>
      )}
    </div>
  );
}
